"use client";
import { useEffect, useRef } from "react";

/**
 * The project screenshot that only exists while you are pointing at something.
 *
 * Nothing about the page changes at rest: this renders one fixed, empty,
 * pointer-transparent panel at zero opacity, and no screenshot is even
 * requested until a pointer actually reaches the projects list. The site looks
 * exactly as it did when nobody is hovering, which was the whole point.
 *
 * How it behaves:
 *
 *   - The panel trails the pointer more slowly than the cursor ring does, so
 *     the ring leads and the image follows it. Two things moving at the same
 *     speed read as one thing that has got fatter; two things moving at
 *     different speeds read as a relationship.
 *   - It tilts with horizontal velocity, a couple of degrees, which is enough
 *     to suggest weight without becoming a party trick.
 *   - It never appears empty. The panel is only revealed once that project's
 *     image has actually decoded, so a slow connection gets nothing rather
 *     than a blank rectangle that fills in late.
 *
 * Driven entirely through refs and rAF. Putting pointer coordinates into React
 * state would re-render this component on every mousemove, and it sits inside
 * the page rather than in a portal.
 */

const WIDTH = 268;
const HEIGHT = 180;
const OFFSET_X = 26;
const OFFSET_Y = 22;

export default function ProjectPreview({ selector = "[data-preview]" }) {
  const panel = useRef(null);
  const image = useRef(null);

  useEffect(() => {
    // No hover on touch, and no cursor to attach to either.
    if (!window.matchMedia("(pointer: fine)").matches) return;

    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const el = panel.current;
    const img = image.current;
    if (!el || !img) return;

    /* src -> Promise<HTMLImageElement>.
     *
     * The *promise* is cached, not the resolved element. Caching the element
     * makes load() return a promise on a miss and a bare <img> on a hit, and
     * then any caller that chains .then/.catch onto it throws the moment
     * something has finished decoding — which is intermittent, because it
     * depends on whether a decode beat an idle callback. */
    const decoded = new Map();
    let current = null; // src we are currently showing
    let raf = 0;

    // Where the pointer is, and where the panel has got to so far.
    const target = { x: -400, y: -400 };
    const at = { x: -400, y: -400 };
    let lastX = -400;
    let tilt = 0;
    let anchored = null; // set when opened by keyboard focus instead of a pointer

    const load = (src) => {
      const cached = decoded.get(src);
      if (cached) return cached;

      const p = new Promise((resolve, reject) => {
        const i = new Image();
        i.decoding = "async";
        i.onload = () => resolve(i);
        i.onerror = () => reject(new Error(`could not load ${src}`));
        i.src = src;
      });
      // Drop a failure from the cache so a later hover can try again rather
      // than replaying the same rejection for the rest of the session.
      decoded.set(
        src,
        p.catch((err) => {
          decoded.delete(src);
          throw err;
        })
      );
      return decoded.get(src);
    };

    const show = async (src) => {
      current = src;
      try {
        await load(src);
      } catch {
        return; // a missing screenshot simply means no preview
      }
      // The pointer may have moved on while that was in flight.
      if (current !== src) return;
      img.src = src;
      el.classList.add("project-preview--on");
    };

    const hide = () => {
      current = null;
      anchored = null;
      el.classList.remove("project-preview--on");
    };

    const frame = () => {
      if (anchored) {
        at.x = anchored.x;
        at.y = anchored.y;
        tilt = 0;
      } else {
        const ease = still ? 1 : 0.12;
        at.x += (target.x - at.x) * ease;
        at.y += (target.y - at.y) * ease;
        if (!still) {
          const vx = target.x - lastX;
          lastX = target.x;
          // Clamped and eased so a fast flick does not spin the panel.
          tilt += (Math.max(-6, Math.min(6, vx * 0.35)) - tilt) * 0.1;
        }
      }
      el.style.transform = `translate3d(${Math.round(at.x)}px, ${Math.round(
        at.y
      )}px, 0) rotate(${tilt.toFixed(2)}deg)`;
      raf = requestAnimationFrame(frame);
    };

    const place = (x, y) => {
      // Keep the panel on screen near the right and bottom edges.
      const maxX = window.innerWidth - WIDTH - 12;
      const maxY = window.innerHeight - HEIGHT - 12;
      target.x = Math.min(x + OFFSET_X, maxX);
      target.y = Math.min(y + OFFSET_Y, maxY);
    };

    // Kept so a scroll can re-test what is now under a stationary pointer.
    const pointer = { x: -400, y: -400, seen: false };

    const onMove = (e) => {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      pointer.seen = true;
      place(e.clientX, e.clientY);
    };

    const consider = (node) => {
      const hit = node?.closest?.(selector);
      if (!hit) {
        if (current) hide();
        return;
      }
      const src = hit.getAttribute("data-preview");
      if (!src || src === current) return;
      anchored = null;
      show(src);
    };

    const onOver = (e) => consider(e.target);

    /* Scrolling moves the list under a pointer that has not itself moved, and
       the browser will not reliably fire pointerover for that. Hiding instead
       would leave the preview gone until you jiggled the mouse, so re-run the
       hit test against the last known pointer position. */
    const onScroll = () => {
      if (!pointer.seen || anchored) return;
      place(pointer.x, pointer.y);
      consider(document.elementFromPoint(pointer.x, pointer.y));
    };

    // Keyboard users get the same thing, parked beside what they focused
    // rather than chasing a pointer that is not there.
    const onFocusIn = (e) => {
      const hit = e.target.closest?.(selector);
      if (!hit) return;
      const src = hit.getAttribute("data-preview");
      if (!src) return;
      const r = hit.getBoundingClientRect();
      anchored = {
        x: Math.min(r.right + 20, window.innerWidth - WIDTH - 12),
        y: Math.min(r.top, window.innerHeight - HEIGHT - 12),
      };
      at.x = anchored.x;
      at.y = anchored.y;
      show(src);
    };

    /* Only tear down if the focus is what opened it. A pointer may still be
       resting on a project, and tabbing away should not blank that. */
    const onFocusOut = (e) => {
      if (anchored && e.target.closest?.(selector)) hide();
    };

    /* Warm the cache when the pointer first reaches the list, so the panel is
       instant on the second project even though nothing was fetched on load. */
    let warmed = false;
    const warm = () => {
      if (warmed) return;
      warmed = true;
      const srcs = [...document.querySelectorAll(selector)]
        .map((n) => n.getAttribute("data-preview"))
        .filter(Boolean);
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
      idle(() => srcs.forEach((s) => load(s).catch(() => {})));
    };

    const onEnterList = (e) => {
      if (e.target.closest?.(selector)) warm();
    };

    document.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("pointerover", onOver, { passive: true });
    document.addEventListener("pointerover", onEnterList, { passive: true });
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    document.addEventListener("mouseleave", hide);
    window.addEventListener("blur", hide);
    window.addEventListener("scroll", onScroll, { passive: true });

    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerover", onOver);
      document.removeEventListener("pointerover", onEnterList);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("mouseleave", hide);
      window.removeEventListener("blur", hide);
      window.removeEventListener("scroll", onScroll);
    };
  }, [selector]);

  return (
    <div className="project-preview" ref={panel} aria-hidden="true">
      <img className="project-preview__img" ref={image} alt="" draggable="false" />
    </div>
  );
}
